import React, { useState, useRef, useEffect } from "react";
import { FiClock, FiChevronDown } from "react-icons/fi";

const CustomTimePicker = ({ value, onChange }) => {
  const [hour, minute] = (value || "00:00").split(":").map(Number);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSelect, setActiveSelect] = useState(null); // 'hour' or 'minute'

  const pickerRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);

  // Tạo danh sách giờ và phút
  const hours = Array.from({ length: 24 }, (_, i) =>
    String(i).padStart(2, "0")
  );

  const minutes = Array.from({ length: 12 }, (_, i) =>
    String(i * 5).padStart(2, "0")
  );

  // Xử lý click bên ngoài để đóng dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveSelect(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cuộn đến giá trị đã chọn khi dropdown mở
  useEffect(() => {
    if (isOpen) {
      if (activeSelect === "hour" && hourRef.current) {
        const selectedOption = hourRef.current.querySelector(
          `[data-value="${String(hour).padStart(2, "0")}"]`
        );
        if (selectedOption) {
          selectedOption.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      } else if (activeSelect === "minute" && minuteRef.current) {
        const selectedOption = minuteRef.current.querySelector(
          `[data-value="${String(minute).padStart(2, "0")}"]`
        );
        if (selectedOption) {
          selectedOption.scrollIntoView({
            block: "center",
            behavior: "smooth",
          });
        }
      }
    }
  }, [isOpen, activeSelect, hour, minute]);

  const handleHourChange = (newHour) => {
    onChange(`${newHour}:${String(minute).padStart(2, "0")}`);
    setIsOpen(false);
    setActiveSelect(null);
  };

  const handleMinuteChange = (newMinute) => {
    onChange(`${String(hour).padStart(2, "0")}:${newMinute}`);
    setIsOpen(false);
    setActiveSelect(null);
  };

  const toggleDropdown = (select) => {
    if (isOpen && activeSelect === select) {
      setIsOpen(false);
      setActiveSelect(null);
    } else {
      setIsOpen(true);
      setActiveSelect(select);
    }
  };

  return (
    <div className="relative w-full" ref={pickerRef}>
      <div className="flex items-center bg-white border border-gray-300 rounded-md shadow-sm hover:border-blue-500 transition-all duration-200">
        <div className="flex items-center px-3 py-2 w-full">
          <FiClock className="text-gray-500 mr-2" />
          <div className="flex items-center justify-center w-full">
            <div
              className={`cursor-pointer px-3 py-1 rounded-md text-center min-w-[30px] flex items-center justify-center ${
                activeSelect === "hour"
                  ? "bg-blue-50 text-blue-600"
                  : "hover:bg-gray-100 text-gray-700"
              }`}
              onClick={() => toggleDropdown("hour")}
              style={{ minWidth: "36px" }}
            >
              {String(hour).padStart(2, "0")}
            </div>
            <span className="text-gray-500 mx-1">:</span>
            <div
              className={`cursor-pointer px-3 py-1 rounded-md text-center min-w-[30px] flex items-center justify-center ${
                activeSelect === "minute"
                  ? "bg-blue-50 text-blue-600"
                  : "hover:bg-gray-100 text-gray-700"
              }`}
              onClick={() => toggleDropdown("minute")}
              style={{ minWidth: "36px" }}
            >
              {String(minute).padStart(2, "0")}
            </div>
          </div>
          <FiChevronDown
            className={`text-gray-500 ml-2 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {isOpen && activeSelect === "hour" && (
        <div
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50"
          ref={hourRef}
        >
          {hours.map((h) => (
            <div
              key={h}
              data-value={h}
              className={`px-4 py-2 cursor-pointer text-center hover:bg-gray-100 transition-colors ${
                h === String(hour).padStart(2, "0")
                  ? "bg-blue-100 text-blue-600 font-medium"
                  : "text-gray-700"
              }`}
              onClick={() => handleHourChange(h)}
            >
              {h}
            </div>
          ))}
        </div>
      )}

      {isOpen && activeSelect === "minute" && (
        <div
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50"
          ref={minuteRef}
        >
          {minutes.map((m) => (
            <div
              key={m}
              data-value={m}
              className={`px-4 py-2 cursor-pointer text-center hover:bg-gray-100 transition-colors ${
                m === String(minute).padStart(2, "0")
                  ? "bg-blue-100 text-blue-600 font-medium"
                  : "text-gray-700"
              }`}
              onClick={() => handleMinuteChange(m)}
            >
              {m}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomTimePicker;
